import React, { useState, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useMatchMedia } from 'js/sign-components/common/hooks';
import PricingLevels from './pricing-levels';
import CouponDisplay from './coupon-display';
import { Stack } from '@dropbox/dig-foundations';
import {
  FormControlGroup,
  FormLabel,
  FormRow,
} from '@dropbox/dig-components/form_row';
import { RadioButton } from '@dropbox/dig-components/controls';
import styles from './index.scss';
import { PRICING_COMPACT_MEDIA_QUERY } from './constants';
import { isAnnualTimePeriod, isMonthlyTimePeriod } from './pricing-period';
import type { PricingAreaProps, TimePeriod } from './types';

const messages = defineMessages({
  errorMessage: {
    id: 'pricing.errorMessage',
    description: 'Error, displayed to user when something went wrong.',
    defaultMessage: 'Sorry, we could not process your payment.',
  },
});

const getBillingPeriodTestId = (value: TimePeriod): string =>
  value === 'Y'
    ? 'pricing-billing-period-annual'
    : 'pricing-billing-period-monthly';

const hiddenCouponDisplayCodes = new Set(['FIRSTFAX']);

function shouldHideCouponDisplay(couponCode?: string | null): boolean {
  return hiddenCouponDisplayCodes.has(couponCode?.trim().toUpperCase() ?? '');
}

const PricingArea: React.FC<PricingAreaProps> = ({
  mustContactSales,
  selectedTimePeriod,
  timePeriodChoices,
  coupon,
  pricingData,
  isApi,
  pricingSource,
  canUseApi,
  canDirectPurchase,
  faxPricingDirectPurchaseOnly,
  experimentCouponCode,
  errors,
  signupForm,
  corpLevelName,
  corporateFeatures,
  changePlanRestricted,
  ...rest
}) => {
  const intl = useIntl();
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(
    selectedTimePeriod || timePeriodChoices[1]?.value || 'M',
  );

  const isAnnual = isAnnualTimePeriod(timePeriod);
  const isMonthly = isMonthlyTimePeriod(timePeriod);

  const hasValidCoupon = useMemo(() => {
    if (!coupon) return false;

    const isCouponEligible = pricingData.billing_plans.some(
      (plan) => plan.coupon,
    );
    if (!isCouponEligible) return false;

    const monthlyDiscount = coupon.monthly_rate ?? 0;
    const annualDiscount = coupon.annual_rate ?? 0;

    if (isMonthly && monthlyDiscount === 0) {
      return false;
    }

    if (isAnnual && annualDiscount === 0) {
      const hasAnnualCouponPricing = pricingData.billing_plans.some(
        (plan) => plan.formatted_coupon_annual_price,
      );
      if (!hasAnnualCouponPricing) {
        return false;
      }
    }

    return !!coupon.coupon_code;
  }, [coupon, pricingData.billing_plans, isMonthly, isAnnual]);

  const errorMessage = useMemo(() => {
    if (!errors) return null;

    let errorMsg = intl.formatMessage(messages.errorMessage);
    if (errors['payment[submit]']) {
      errorMsg += ` ${errors['payment[submit]']}`;
    }
    if (errors['payment[cc_number]']) {
      errorMsg += ` ${errors['payment[cc_number]']}`;
    }
    if (errors['payment[0]']) {
      errorMsg += ` ${errors['payment[0]']}`;
    }

    return (
      <div data-test-id="errors" className="row text-center l-padding-v-20">
        <p className="c-red">{errorMsg}</p>
      </div>
    );
  }, [errors, intl]);

  const shouldShowCouponDisplay =
    hasValidCoupon && !shouldHideCouponDisplay(coupon?.coupon_code);

  const radioButtonId = (value: TimePeriod) => `time-period-${value}`;
  const isCompactPricing = useMatchMedia(PRICING_COMPACT_MEDIA_QUERY);

  const billingToggle =
    timePeriodChoices.length > 1 ? (
      <FormControlGroup className={styles.billingToggle}>
        <FormRow variant="control">
          {timePeriodChoices.map((choice) => (
            <React.Fragment key={choice.value}>
              <RadioButton
                className={styles.radioButton}
                id={radioButtonId(choice.value)}
                name="option"
                value={choice.value}
                checked={timePeriod === choice.value}
                onChange={() => setTimePeriod(choice.value as TimePeriod)}
              />
              <FormLabel
                htmlFor={radioButtonId(choice.value)}
                variant="control"
                data-testid={getBillingPeriodTestId(choice.value)}
              >
                Billed {choice.name.toLowerCase()}
              </FormLabel>
            </React.Fragment>
          ))}
        </FormRow>
      </FormControlGroup>
    ) : null;

  return (
    <Stack className={`row ${styles.pricingArea}`} gap="16" paddingBottom="16">
      {shouldShowCouponDisplay && coupon && (
        <CouponDisplay
          couponCode={coupon.coupon_code}
          couponDescription={coupon.description}
        />
      )}
      {billingToggle && !isCompactPricing && billingToggle}
      {errorMessage}
      <PricingLevels
        billingToggle={billingToggle}
        isCompact={isCompactPricing}
        hasValidCoupon={hasValidCoupon}
        couponNumMonths={coupon?.num_months}
        billingPlans={pricingData.billing_plans}
        timePeriod={timePeriod}
        signupForm={signupForm || {}}
        isApi={isApi || false}
        pricingData={pricingData}
        canDirectPurchase={canDirectPurchase || false}
        faxPricingDirectPurchaseOnly={faxPricingDirectPurchaseOnly || false}
        experimentCouponCode={experimentCouponCode}
        corpLevelName={corpLevelName || ''}
        corporateFeatures={corporateFeatures}
        changePlanRestricted={changePlanRestricted || false}
        {...rest}
      />
    </Stack>
  );
};

export default PricingArea;
